import { api } from './index';

export interface CardPayConversionState {
  endDate: {
    date: string | null;
    displayMode: string | null;
    hubspotTicketId: string | null;
    status: string | null;
    syncedAt: string | null;
  };
  membership: {
    companyId: string;
    companyName: string;
    companySelfPay: boolean;
    domainMember: boolean;
    id: string;
    paymentStatus: string;
  } | null;
  paymentCheck?: {
    latestInvoiceStatus: string | null;
    paid: boolean;
    subscriptionStatus: string;
  };
  steps: {
    cardPay: boolean;
    endDateOngoing: boolean;
    paymentConfirmed: boolean;
    stripeLinked: boolean;
  };
  stripe: {
    customerId: string | null;
    plan: string | null;
    status: string | null;
    subscriptionId: string | null;
  } | null;
}

type StepAction = 'convert' | 'resync_end_date' | 'verify_payment';

const conversionUrl = (userId: string) =>
  `/users/${userId}/card_pay_conversion`;

export const cardPayConversionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCardPayConversion: builder.query<CardPayConversionState, string>({
      query: (userId) => ({ method: 'GET', url: conversionUrl(userId) }),
    }),
    linkConversionStripe: builder.mutation<
      CardPayConversionState,
      { customerId: string; userId: string }
    >({
      // Every step returns the full state, so write it straight into the cached query.
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cardPayConversionApi.util.upsertQueryData(
              'getCardPayConversion',
              userId,
              data,
            ),
          );
        } catch {
          // The caller shows the error from the mutation result.
        }
      },
      query: ({ customerId, userId }) => ({
        data: { customerId },
        method: 'POST',
        url: `${conversionUrl(userId)}/link_stripe`,
      }),
    }),
    runConversionStep: builder.mutation<
      CardPayConversionState,
      { sendToPaywall?: boolean; step: StepAction; userId: string }
    >({
      // Converting changes what the member profile shows (plan, paid flags).
      invalidatesTags: (_result, _error, { step }) =>
        step === 'convert' ? ['User'] : [],
      // Every step returns the full state, so write it straight into the cached query.
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            cardPayConversionApi.util.upsertQueryData(
              'getCardPayConversion',
              userId,
              data,
            ),
          );
        } catch {
          // The caller shows the error from the mutation result.
        }
      },
      query: ({ sendToPaywall, step, userId }) => ({
        data: step === 'convert' ? { sendToPaywall: !!sendToPaywall } : {},
        method: 'POST',
        url: `${conversionUrl(userId)}/${step}`,
      }),
    }),
  }),
});

export const {
  useGetCardPayConversionQuery,
  useLinkConversionStripeMutation,
  useRunConversionStepMutation,
} = cardPayConversionApi;
